import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { Routes as AppRoutes } from "../../../routes";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import CustomDropdown from "../../../components/shared/dropdown/CustomDropdown";
import Button from "../../../components/shared/buttons/button";
import Loader from "../../../components/shared/loader/Loader";
import HospitalCoveragePicker from "../../../components/va-distributors/HospitalCoveragePicker";
import {
  useGetVaDistributorQuery,
  useUpdateVaDistributorMutation,
} from "../../../app/vaApi/vaApi";
import { useVaCommissions } from "../../../hooks/useVaCommissions";
import { useVaHospitals } from "../../../hooks/useVaHospitals";
import {
  clearVaDistributorDetailsId,
  getVaDistributorDetailsId,
} from "../../../helpers/vaDistributorNavigation";
import { showToast } from "../../../helpers/Utils";
import { AngleRightSvg } from "../../../svgFiles/AngleRightSvg";
import { ExclamationSvg } from "../../../svgFiles/ExclamationSvg";
import { CrossSvg } from "../../../svgFiles/CrossSvg";
import { CircleCheckSvg } from "../../../svgFiles/CircleCheckSvg";
import PasswordInput, {
  isValidLoginPassword,
} from "../../../components/va-distributors/PasswordInput";
import "./vaDistributors.css";

const VA_NAME_MAX_LENGTH = 50;

const CreditCardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="#98A2B3" strokeWidth="1.2" />
    <path d="M1.5 6.5H14.5" stroke="#98A2B3" strokeWidth="1.2" />
    <path d="M4 10H7" stroke="#98A2B3" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

function getCommissionPercent(distributor) {
  const value =
    distributor?.commissionPercent ??
    distributor?.commission?.percent ??
    distributor?.commission?.id;
  return value === "" || value == null ? "" : String(value);
}

function formFromDistributor(distributor) {
  const hospitalIds = Array.isArray(distributor.hospitalIds)
    ? distributor.hospitalIds.map(String)
    : [];
  const payment =
    distributor?.paymentMethod && typeof distributor.paymentMethod === "object"
      ? distributor.paymentMethod
      : {};
  return {
    name: distributor.name || "",
    email: distributor.email || "",
    phone: distributor.phone || "",
    hospitalIds,
    commissionPercent: getCommissionPercent(distributor),
    password: "",
    confirmPassword: "",
    accountHolderName:
      String(payment.accountHolderName || "").trim() ||
      [
        payment.accountHolderFirstName,
        payment.accountHolderLastName,
      ]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" "),
    accountNumber:
      payment.accountNumber ||
      payment.cardNumber ||
      distributor.cardNumber ||
      "",
    routingNumber: payment.routingNumber || distributor.routingNumber || "",
    accountType: payment.accountType || "checking",
  };
}

function normalizeForCompare(form) {
  return {
    ...form,
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    hospitalIds: [...(form.hospitalIds || [])].map(String).sort(),
    commissionPercent:
      form.commissionPercent === "" || form.commissionPercent == null
        ? ""
        : String(form.commissionPercent),
    password: form.password || "",
    confirmPassword: form.confirmPassword || "",
    accountHolderName: form.accountHolderName.trim(),
    accountNumber: form.accountNumber.trim(),
    routingNumber: form.routingNumber.trim(),
    accountType: form.accountType || "checking",
  };
}

const ACCOUNT_TYPE_OPTIONS = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
];

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function hasAnyPaymentField(form) {
  return Boolean(
    form.accountHolderName?.trim() ||
      form.accountNumber?.trim() ||
      form.routingNumber?.trim()
  );
}

function clearPaymentMethodError(setFieldErrors) {
  setFieldErrors((prev) => {
    if (!prev.paymentMethod) return prev;
    const next = { ...prev };
    delete next.paymentMethod;
    return next;
  });
}

export default function VaDistributorDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = getVaDistributorDetailsId(location);

  useEffect(() => {
    if (!id) {
      navigate(AppRoutes.VaDistributorsAdmin.path, { replace: true });
    }
  }, [id, navigate]);

  const { data, isLoading, isError, refetch } = useGetVaDistributorQuery(id, {
    refetchOnMountOrArgChange: true,
    skip: !id,
  });
  const [updateDistributor, { isLoading: isSaving }] = useUpdateVaDistributorMutation();
  const { commissions } = useVaCommissions();
  const { stateGroups, isLoading: loadingHospitals } = useVaHospitals({
    skip: !id,
  });

  const distributor = data?.data ?? data?.item ?? null;
  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initializedIdRef = useRef(null);
  const savedFormRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    savedFormRef.current = savedForm;
    formRef.current = form;
  }, [savedForm, form]);

  useEffect(() => {
    if (!distributor || String(distributor.id) !== String(id)) return;

    const initial = formFromDistributor(distributor);
    const isNewId = initializedIdRef.current !== id;

    if (isNewId) {
      initializedIdRef.current = id;
      setForm(initial);
      setSavedForm(initial);
      setFieldErrors({});
      return;
    }

    // Re-hydrate from API when user has no unsaved edits (e.g. paymentMethod
    // arrived after an earlier empty cache/init).
    const prevForm = formRef.current;
    const prevSaved = savedFormRef.current;
    if (!prevForm || !prevSaved) {
      setForm(initial);
      setSavedForm(initial);
      return;
    }

    const isDirty =
      JSON.stringify(normalizeForCompare(prevForm)) !==
      JSON.stringify(normalizeForCompare(prevSaved));
    if (!isDirty) {
      setForm(initial);
      setSavedForm(initial);
    }
  }, [id, distributor]);

  const dirty = useMemo(() => {
    if (!form || !savedForm) return false;
    return JSON.stringify(normalizeForCompare(form)) !== JSON.stringify(normalizeForCompare(savedForm));
  }, [form, savedForm]);

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    else if (form.name.trim().length > VA_NAME_MAX_LENGTH) {
      errors.name = `Name must be ${VA_NAME_MAX_LENGTH} characters or fewer`;
    }
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Enter a valid email";
    }
    const validHospitalIds = (form.hospitalIds || []).map(String).filter(Boolean);
    if (!validHospitalIds.length) {
      errors.hospitalIds = "Select at least one hospital";
    }
    if (form.commissionPercent === "" || form.commissionPercent == null) {
      errors.commissionPercent = "Commission is required";
    }

    const passwordEntered = Boolean(form.password || form.confirmPassword);
    if (passwordEntered) {
      if (!form.password) {
        errors.password = "Enter a new password or leave both fields blank";
      } else if (!isValidLoginPassword(form.password)) {
        errors.password =
          "Password must be 8–50 characters with upper, lower, number, and special character.";
      }
      if (!form.confirmPassword) {
        errors.confirmPassword = "Confirm password is required";
      } else if (form.password !== form.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

    const hadBankAccount = savedForm ? hasAnyPaymentField(savedForm) : false;
    const clearedBankAccount = !hasAnyPaymentField(form);

    // Existing bank details cannot be wiped by emptying every field and saving.
    if (hadBankAccount && clearedBankAccount) {
      errors.paymentMethod =
        "Bank account fields cannot be left blank. Fill in the bank details to save.";
    } else if (hasAnyPaymentField(form)) {
      if (!form.accountHolderName.trim()) {
        errors.accountHolderName = "Name is required";
      }
      const routing = digitsOnly(form.routingNumber);
      if (routing.length !== 9) {
        errors.routingNumber = "Routing number must be 9 digits";
      }
      const account = digitsOnly(form.accountNumber);
      if (account.length < 4 || account.length > 17) {
        errors.accountNumber = "Account number must be 4–17 digits";
      }
      if (!["checking", "savings"].includes(form.accountType)) {
        errors.accountType = "Select Checking or Savings";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      id,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      hospitalIds: (form.hospitalIds || []).map(String).filter(Boolean),
      commissionPercent: Number(form.commissionPercent),
    };

    if (hasAnyPaymentField(form)) {
      payload.paymentMethod = {
        accountHolderName: form.accountHolderName.trim(),
        accountNumber: digitsOnly(form.accountNumber),
        routingNumber: digitsOnly(form.routingNumber),
        accountType: form.accountType || "checking",
      };
    }

    if (form.password) {
      payload.password = form.password;
    }

    try {
      await updateDistributor(payload).unwrap();
      clearVaDistributorDetailsId();
      showToast(
        form.password
          ? "Distributor and password saved successfully"
          : "Distributor saved successfully",
        "success"
      );
      navigate(AppRoutes.VaDistributorsAdmin.path);
    } catch (err) {
      const msg = err?.data?.message || "Something went wrong";
      const field = err?.data?.field;
      if (field) setFieldErrors((prev) => ({ ...prev, [field]: msg }));
      else showToast(msg, "error");
    }
  };

  const handleDiscard = () => {
    setForm(savedForm);
    setFieldErrors({});
  };

  if (!id) {
    return (
      <div className="va-distributors-page d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="va-distributors-page d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (isError || !distributor) {
    return (
      <div className="va-distributors-page text-center py-5">
        <p className="text-danger mb-3">Failed to load distributor</p>
        <Button
          label="Back to list"
          className="cancel"
          onClick={() => {
            clearVaDistributorDetailsId();
            navigate(AppRoutes.VaDistributorsAdmin.path);
          }}
        />
        <Button label="Retry" className="ms-2" onClick={refetch} />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="va-distributors-page d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  const saveBar = dirty ? (
    <div className="va-distributors-save-bar va-distributors-save-bar--dirty d-flex gap-2 align-items-center">
      <ExclamationSvg />
      <span className="flex-grow-1">Unsaved changes</span>
      <Button
        className="px-0 py-1 justify-content-center cancel bg-white"
        onClick={handleDiscard}
        icon={<CrossSvg />}
        disabled={isSaving}
      />
      <Button
        onClick={handleSave}
        className="px-2 py-1"
        label={isSaving ? "Saving..." : "Save"}
        icon={<CircleCheckSvg />}
        disabled={isSaving}
      />
    </div>
  ) : null;

  return (
    <div className="va-distributors-page va-distributor-details-page">
      {saveBar ? createPortal(saveBar, document.body) : null}

      <ul className="va-distributors-breadcrumbs p-0 d-flex gap-2 align-items-center mb-3">
        <li
          role="button"
          className="va-distributors-breadcrumbs__link"
          onClick={() => {
            clearVaDistributorDetailsId();
            navigate(AppRoutes.VaDistributorsAdmin.path);
          }}
        >
          Distributors
        </li>
        <li className="va-distributors-breadcrumbs__sep">
          <AngleRightSvg />
        </li>
        <li className="va-distributors-breadcrumbs__current">
          {form.name || distributor.name}
        </li>
      </ul>

      <div className="va-distributor-details-card mb-3">
        <h2 className="va-distributor-details-card__title">Distributor Details</h2>
        <InputWithLabel
          label="Distributor"
          name="name"
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              name: e.target.value.slice(0, VA_NAME_MAX_LENGTH),
            }))
          }
          placeholder="Enter name"
          maxLength={VA_NAME_MAX_LENGTH}
          required
        />
        {fieldErrors.name && (
          <p className="text-danger small mt-n2 mb-2">{fieldErrors.name}</p>
        )}
        <HospitalCoveragePicker
          hospitalIds={form.hospitalIds}
          onChange={(hospitalIds) =>
            setForm((prev) => ({ ...prev, hospitalIds }))
          }
          error={fieldErrors.hospitalIds}
          stateGroups={stateGroups}
          isLoading={loadingHospitals}
          currentDistributorId={id}
        />

        <div className="row g-2">
          <div className="col-md-6">
            <InputWithLabel
              label="Email"
              type="email"
              name="email"
              value={form.email}
              placeholder="name@company.com"
              disabled
            />
          </div>
          <div className="col-md-6">
            <InputWithLabel
              label="Phone Number"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="Enter phone number"
            />
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="cmn_label d-block">Password</label>
            <PasswordInput
              name="password"
              value={form.password}
              onChange={(e) => {
                const password = e.target.value;
                setForm((prev) => ({ ...prev, password }));
                setFieldErrors((prev) => {
                  if (!prev.password) return prev;
                  const next = { ...prev };
                  delete next.password;
                  return next;
                });
              }}
              placeholder="Enter new password (optional)"
              error={
                !!fieldErrors.password ||
                (Boolean(form.password) && !isValidLoginPassword(form.password))
              }
            />
            {(fieldErrors.password ||
              (form.password && !isValidLoginPassword(form.password))) && (
              <p className="text-danger small">
                {fieldErrors.password ||
                  "Password must be 8–50 characters with upper, lower, number, and special character."}
              </p>
            )}
          </div>
          <div className="col-md-6">
            <label className="cmn_label d-block">
              Confirm Password
              {form.password ? <span className="text-danger"> *</span> : null}
            </label>
            <PasswordInput
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  confirmPassword: e.target.value,
                }))
              }
              placeholder="Re-enter new password"
              error={!!fieldErrors.confirmPassword}
            />
            {fieldErrors.confirmPassword && (
              <p className="text-danger small">{fieldErrors.confirmPassword}</p>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="cmn_label d-block">
            Commission <span className="text-danger">*</span>
          </label>
          <CustomDropdown
            label="Select Commission"
            value={form.commissionPercent}
            onSelect={(opt) =>
              setForm((prev) => ({ ...prev, commissionPercent: opt.value }))
            }
            options={commissions.map((c) => ({
              value: String(c.percent ?? c.id),
              label: c.label,
            }))}
            error={!!fieldErrors.commissionPercent}
            className="w-100"
          />
          {fieldErrors.commissionPercent && (
            <p className="text-danger small">{fieldErrors.commissionPercent}</p>
          )}
        </div>
      </div>

      <div className="va-distributor-details-card">
        <h2 className="va-distributor-details-card__title">Payment methods</h2>
        <p className="text-muted small mb-3">
          Bank details are required for Stripe commission payouts.
        </p>
        {fieldErrors.paymentMethod && (
          <p className="text-danger small mb-3">{fieldErrors.paymentMethod}</p>
        )}
        <div className="row g-2">
          <div className="col-12">
            <InputWithLabel
              label="Name"
              name="accountHolderName"
              value={form.accountHolderName}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  accountHolderName: e.target.value,
                }));
                clearPaymentMethodError(setFieldErrors);
              }}
              placeholder="Account holder name"
            />
            {fieldErrors.accountHolderName && (
              <p className="text-danger small">
                {fieldErrors.accountHolderName}
              </p>
            )}
          </div>
          <div className="col-md-6">
            <label className="cmn_label d-block">
              Type <span className="text-danger">*</span>
            </label>
            <CustomDropdown
              label="Select type"
              value={form.accountType}
              onSelect={(opt) => {
                setForm((prev) => ({ ...prev, accountType: opt.value }));
                clearPaymentMethodError(setFieldErrors);
              }}
              options={ACCOUNT_TYPE_OPTIONS}
              error={!!fieldErrors.accountType}
              className="w-100"
            />
            {fieldErrors.accountType && (
              <p className="text-danger small">{fieldErrors.accountType}</p>
            )}
          </div>
          <div className="col-md-6">
            <InputWithLabel
              label="Routing number"
              name="routingNumber"
              value={form.routingNumber}
              onChange={(e) => {
                const routingNumber = digitsOnly(e.target.value).slice(0, 9);
                setForm((prev) => ({ ...prev, routingNumber }));
                setFieldErrors((prev) => {
                  if (!prev.paymentMethod && !prev.routingNumber) return prev;
                  const next = { ...prev };
                  delete next.paymentMethod;
                  delete next.routingNumber;
                  return next;
                });
              }}
              placeholder="9-digit routing number"
              maxLength={9}
              inputMode="numeric"
              pattern="[0-9]{9}"
              error={!!fieldErrors.routingNumber}
            />
            {fieldErrors.routingNumber && (
              <p className="text-danger small">{fieldErrors.routingNumber}</p>
            )}
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label className="cmn_label">Account number</Form.Label>
              <InputGroup>
                <InputGroup.Text className="va-distributor-details-card__card-icon">
                  <CreditCardIcon />
                </InputGroup.Text>
                <Form.Control
                  className="cmn_input"
                  type="text"
                  name="accountNumber"
                  value={form.accountNumber}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      accountNumber: e.target.value,
                    }));
                    clearPaymentMethodError(setFieldErrors);
                  }}
                  placeholder="Enter bank account number"
                  isInvalid={!!fieldErrors.accountNumber}
                />
              </InputGroup>
              {fieldErrors.accountNumber && (
                <p className="text-danger small">{fieldErrors.accountNumber}</p>
              )}
            </Form.Group>
          </div>
        </div>
      </div>
    </div>
  );
}
